// Copied from usehooks-ts.com and modified for Uthana

import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";

import { useEventCallback } from "@/lib/useEventCallback";
import { useEventListener } from "@/lib/useEventListener";
import { safeParseBool, safeParseInt } from "@/lib/utils";
import type { OkAny } from "@/types/OkAny.type";

declare global {
  interface WindowEventMap {
    "local-storage": CustomEvent;
  }
}

type UseLocalStorageOptions = {
  dataType?: "json" | "string" | "int" | "boolean";
  initializeWithValue?: boolean;
  debug?: boolean;
};
// Debug switch: when true, completely bypass localStorage reads/writes.
// Does NOT clear keys; just avoids touching the API and returns defaults/state.
// Toggle by editing this constant or wiring to an env var if desired.
const DEBUG_DISABLE_LOCAL_STORAGE = false;

const tryParse = <T = OkAny>(
  key: string,
  val: OkAny,
  fallback: T | null = null,
  dataType: UseLocalStorageOptions["dataType"] = "string",
) => {
  if (val === null) return fallback;
  if (val === undefined) return fallback;
  if (dataType === "string" || !dataType) return val;
  if (dataType === "int") return safeParseInt(val, { fallback: fallback, onError: "throw" });
  if (dataType === "boolean") return safeParseBool(val, { fallback: Boolean(fallback), onError: "throw" });
  try {
    return val ? JSON.parse(val) : fallback;
  } catch (e) {
    console.error(e);
    console.error(`Failed to parse LocalStorage key '${key}': '${val}'\n`);
    throw e;
  }
};

const trySerialize = (key: string, val: OkAny, dataType: UseLocalStorageOptions["dataType"] = "string") => {
  if (val === null) return null;
  if (val === undefined) return undefined;
  if (dataType === "json") {
    try {
      return JSON.stringify(val);
    } catch (e) {
      console.error(e);
      console.error(`Failed to serialize LocalStorage key '${key}': '${val}'\n`);
      throw e;
    }
  }
  return String(val);
};

/** Use local storage. A drop-in replacement for useState that persists the value to (and loads from) localStorage.
 * @param key The key to use.
 * @param initialValue The initial value to use. Can be a function to return a value lazily.
 * @param options The options to use.
 * @returns The value from local storage, a setter function to update the value, and a function to remove the value.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T | (() => T),
  options: UseLocalStorageOptions = {},
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const { debug = false, dataType = "string", initializeWithValue = true } = options;

  const serializer = useCallback<(value: T) => string | undefined | null>(
    (value) => trySerialize(key, value, dataType),
    [dataType, key],
  );

  const deserializer = useCallback<(value: string) => T>(
    (value) => {
      // Support 'undefined' as a value
      if (value === "undefined") {
        return undefined as unknown as T;
      }

      const defaultValue = initialValue instanceof Function ? initialValue() : initialValue;

      return tryParse(key, value, defaultValue, dataType);
    },
    [initialValue, key, dataType],
  );

  // Get from local storage then
  // parse stored json or return initialValue
  const readValue = useCallback((): T => {
    const initialValueToUse = initialValue instanceof Function ? initialValue() : initialValue;

    if (typeof window === "undefined" || DEBUG_DISABLE_LOCAL_STORAGE) {
      return initialValueToUse;
    }

    try {
      const raw = window.localStorage.getItem(key);
      if (debug) console.log(`useLocalStorage get: ${key} = ${raw}`);
      return raw ? deserializer(raw) : initialValueToUse;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValueToUse;
    }
  }, [initialValue, key, debug, deserializer]);

  const [storedValue, setStoredValue] = useState(() => {
    if (initializeWithValue) {
      return readValue();
    }

    return initialValue instanceof Function ? initialValue() : initialValue;
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue: Dispatch<SetStateAction<T>> = useEventCallback((value) => {
    if (typeof window === "undefined") {
      console.warn(`Tried setting localStorage key “${key}” even though environment is not a client`);
    }

    try {
      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(readValue()) : value;

      // Save state
      setStoredValue(newValue);

      if (!DEBUG_DISABLE_LOCAL_STORAGE) {
        // Save to local storage
        const serializedValue = serializer(newValue);
        if (debug) console.log(`useLocalStorage set: ${key} = ${serializedValue}`);
        if (serializedValue === null || serializedValue === undefined) {
          window.localStorage.removeItem(key);
        } else {
          window.localStorage.setItem(key, serializedValue);
        }

        // Notify other hook instances
        window.dispatchEvent(new StorageEvent("local-storage", { key }));
      }
    } catch (error) {
      console.warn(`Error setting localStorage key “${key}”:`, error);
    }
  });

  const removeValue = useEventCallback(() => {
    if (typeof window === "undefined") {
      console.warn(`Tried removing localStorage key “${key}” even though environment is not a client`);
    }

    const defaultValue = initialValue instanceof Function ? initialValue() : initialValue;

    // Save state with default value
    setStoredValue(defaultValue);

    if (!DEBUG_DISABLE_LOCAL_STORAGE) {
      // Remove the key from local storage
      window.localStorage.removeItem(key);
      // Notify other hook instances
      window.dispatchEvent(new StorageEvent("local-storage", { key }));
    }
  });

  // Only re-sync from localStorage when the key changes; avoid extra setState loops
  useEffect(() => {
    if (DEBUG_DISABLE_LOCAL_STORAGE) return; // skip IO while debug disabled
    const next = readValue();
    setStoredValue((prev) => (Object.is(prev, next) ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readValue]);

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if (DEBUG_DISABLE_LOCAL_STORAGE) return;
      if ((event as StorageEvent).key && (event as StorageEvent).key !== key) return;
      const next = readValue();
      setStoredValue((prev) => (Object.is(prev, next) ? prev : next));
    },
    [key, readValue],
  );

  useEventListener("storage", handleStorageChange);
  useEventListener("local-storage", handleStorageChange);

  return [storedValue, setValue, removeValue];
}

/**
 * Allows you to get a value from localStorage with the same data models supported by the useLocalStorage hook.
 * Primarily used inside of Redux - not recommended for use inside of React components.
 * @param key
 * @param fallback
 * @param options
 * @returns
 */
export const getFromLocalStorage = <T = OkAny>(
  key: string,
  fallback: T | null = null,
  options: UseLocalStorageOptions = {},
): T => {
  const { dataType = "string" } = options;

  if (typeof window === "undefined" || DEBUG_DISABLE_LOCAL_STORAGE) {
    return fallback as T;
  }

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? tryParse(key, raw, fallback, dataType) : (fallback as T);
  } catch (error) {
    console.warn(`Error reading localStorage key “${key}”:`, error);
    return fallback as T;
  }
};

/**
 * Non-reactive setter for localStorage supporting the same serialization as useLocalStorage.
 */
export const writeToLocalStorage = (key: string, value: OkAny, options: UseLocalStorageOptions = {}) => {
  const { dataType = "string", debug = false } = options;
  if (typeof window === "undefined" || DEBUG_DISABLE_LOCAL_STORAGE) return;
  try {
    const serializedValue = trySerialize(key, value, dataType);
    if (debug) console.log(`setLocalStorage: ${key} = ${serializedValue}`);
    if (serializedValue === null || serializedValue === undefined) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, serializedValue);
    }
    window.dispatchEvent(new StorageEvent("local-storage", { key }));
  } catch (error) {
    console.warn(`Error setting localStorage key “${key}”:`, error);
  }
};
